import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CpoCdrsPanel from './CpoCdrsPanel';
import CpoLocationsPanel from './CpoLocationsPanel';
import CpoSessionsPanel from './CpoSessionsPanel';
import CpoTariffsPanel from './CpoTariffsPanel';

/**
 * CpoRoleOperations component for managing CPO-specific actions.
 * @param {object} properties - Component properties.
 * @param {string} properties.countryCode - The country code.
 * @param {string} properties.partyId - The party ID.
 * @param {string} [properties.partnerCountryCode] - The partner's country code.
 * @param {string} [properties.partnerPartyId] - The partner's party ID.
 * @returns {React.ReactElement} The rendered component.
 */
export default function CpoRoleOperations({
  countryCode,
  partyId,
  partnerCountryCode,
  partnerPartyId,
}: Readonly<{
  countryCode: string;
  partyId: string;
  partnerCountryCode?: string;
  partnerPartyId?: string;
}>): React.ReactElement {
  const { t } = useTranslation('ocpi');
  const [activeModule, setActiveModule] = useState('Locations');

  const modules = [
    { id: 'Locations', name: t('locations'), eligible: '1/1' },
    { id: 'Tariffs', name: t('tariffs'), eligible: '1/1' },
    { id: 'Sessions', name: t('sessions'), eligible: '1/1' },
    { id: 'Cdrs', name: t('cdrs'), eligible: '1/1' },
  ];

  /**
   * Renders the active module panel.
   * @returns {React.ReactElement} The active module panel component.
   */
  const renderActiveModule = (): React.ReactElement => {
    switch (activeModule) {
      case 'Locations':
        return <CpoLocationsPanel countryCode={countryCode} partyId={partyId} />;
      case 'Tariffs':
        return <CpoTariffsPanel countryCode={countryCode} partyId={partyId} />;
      case 'Sessions':
        return (
          <CpoSessionsPanel
            countryCode={countryCode}
            partyId={partyId}
            partnerCountryCode={partnerCountryCode}
            partnerPartyId={partnerPartyId}
          />
        );
      case 'Cdrs':
        return (
          <CpoCdrsPanel
            countryCode={countryCode}
            partyId={partyId}
            partnerCountryCode={partnerCountryCode}
            partnerPartyId={partnerPartyId}
          />
        );
      default:
        return (
          <div className="cpo-module-placeholder">
            <p>
              {t('moduleOperationsUnderConstruction', {
                module: modules.find((m) => m.id === activeModule)?.name || activeModule,
              })}
            </p>
          </div>
        );
    }
  };

  return (
    <>
      {/* ── Role Detail Card ─────────────────────────────────────────── */}
      <div className="operations-role-card">
        <div className="role-card-header">
          <div>
            <h3>{t('cpoRoleOperations')}</h3>
            <p>
              {t('country')}: {countryCode} | {t('party')}: {partyId}
            </p>
          </div>
          <div className="role-badges-right">
            <span className="badge badge-primary">CPO</span>
            <span className="badge badge-gray">
              {countryCode}/{partyId}
            </span>
            <span className="badge badge-gray">
              {t('ownedModules')}: {modules.length}
            </span>
            <span className="badge badge-success">{t('connections')}: 1</span>
          </div>
        </div>

        <div className="modules-selection">
          <h4>{t('ownedModules')}</h4>
          <p className="subtitle">{t('chooseWorkspace')}</p>
          <div className="modules-cards-row">
            {modules.map((module_) => (
              <div
                key={module_.id}
                className={`module-card ${activeModule === module_.id ? 'active' : ''}`}
                onClick={() => setActiveModule(module_.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveModule(module_.id);
                  }
                }}>
                <h5>{module_.name}</h5>
                <span>{module_.name}</span>
                <p>
                  {t('eligible')}: {module_.eligible}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cpo-active-module-wrapper">{renderActiveModule()}</div>
    </>
  );
}
